import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, X } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { SortableImageGallery } from "@/components/admin/SortableImageGallery";
import { getAdminToken } from "@/lib/admin/session";
import { listRecipes, upsertRecipe, deleteRecipe, listProducts } from "@/lib/admin/cms.functions";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SpecCombobox } from "@/components/admin/SpecCombobox";

export const Route = createFileRoute("/admin/ricette/")({
  head: () => ({ meta: [{ title: "Ricette · Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminRecipes,
});

type Recipe = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  category: string;
  cover_image: string | null;
  gallery: string[];
  difficulty: "Facile" | "Media" | "Esperta" | null;
  time_minutes: number | null;
  servings: number | null;
  featured: boolean;
  intro: string;
  ingredients: { qty: string; item: string }[];
  steps: string[];
  chef_tips: string | null;
  pairings: string | null;
  product_slugs: string[];
  status: "published" | "draft";
};

function empty(): Recipe {
  return {
    id: "", slug: "", title: "", subtitle: "", category: "Carni rosse",
    cover_image: null, gallery: [], difficulty: "Facile",
    time_minutes: 30, servings: 4, featured: false, intro: "",
    ingredients: [], steps: [], chef_tips: "", pairings: "",
    product_slugs: [], status: "published",
  };
}

function AdminRecipes() {
  const fetchList = useServerFn(listRecipes);
  const save = useServerFn(upsertRecipe);
  const remove = useServerFn(deleteRecipe);
  const fetchProducts = useServerFn(listProducts);
  const [items, setItems] = useState<Recipe[]>([]);
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<{ slug: string; title: string }[]>([]);

  async function refresh() {
    const token = getAdminToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetchList({ data: { token } });
      setItems(res.recipes as unknown as Recipe[]);
    } finally { setLoading(false); }
  }
  useEffect(() => {
    refresh();
    const token = getAdminToken();
    if (token) fetchProducts({ data: { token } }).then((r) => setProducts(r.products as { slug: string; title: string }[])).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  async function handleSave() {
    if (!editing) return;
    const token = getAdminToken();
    if (!token || !editing.title.trim()) { toast.error("Titolo richiesto"); return; }
    try {
      await save({
        data: {
          token, id: editing.id || null,
          recipe: {
            slug: editing.slug || null,
            title: editing.title,
            subtitle: editing.subtitle || null,
            category: editing.category,
            cover_image: editing.cover_image || null,
            gallery: editing.gallery,
            difficulty: editing.difficulty,
            time_minutes: editing.time_minutes,
            servings: editing.servings,
            featured: editing.featured,
            intro: editing.intro,
            ingredients: editing.ingredients.filter((i) => i.item),
            steps: editing.steps.filter(Boolean),
            chef_tips: editing.chef_tips || null,
            pairings: editing.pairings || null,
            product_slugs: editing.product_slugs,
            status: editing.status,
          },
        },
      });
      toast.success("Ricetta salvata");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  async function handleDelete(id: string) {
    if (!confirm("Eliminare questa ricetta?")) return;
    const token = getAdminToken(); if (!token) return;
    try { await remove({ data: { token, id } }); toast.success("Eliminata"); refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Errore"); }
  }

  return (
    <AdminShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl">Ricette</h1>
            <p className="text-sm text-slate-600">{items.length} ricette</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setEditing(empty())} className="bg-brand-red hover:bg-brand-red-hover">
              <Plus className="h-4 w-4 mr-2" /> Nuova ricetta
            </Button>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? <div className="p-8 text-center text-slate-500">Caricamento...</div>
          : items.length === 0 ? <div className="p-8 text-center text-slate-500">Nessuna ricetta</div>
          : <div className="divide-y divide-slate-200">
              {items.map((r) => (
                <div key={r.id} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <div className="w-14 h-14 rounded bg-slate-100 overflow-hidden shrink-0">
                    {r.cover_image && <img src={r.cover_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{r.title}</span>
                      {r.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                    </div>
                    <div className="text-xs text-slate-500">{r.category} · {r.difficulty} · {r.time_minutes}min</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              ))}
            </div>}
        </div>

        <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
            <SheetHeader><SheetTitle>{editing?.id ? "Modifica ricetta" : "Nuova ricetta"}</SheetTitle></SheetHeader>
            {editing && (
              <div className="mt-6 space-y-4 pb-12">
                <div><Label>Titolo *</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label>Sottotitolo</Label><Input value={editing.subtitle ?? ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></div>
                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Categoria</Label>
                    <SpecCombobox
                      value={editing.category}
                      onChange={(v) => setEditing({ ...editing, category: v })}
                      options={Array.from(new Set(items.map((r) => r.category).filter(Boolean))).sort()}
                      placeholder="Seleziona o crea..."
                      createLabel="Crea categoria"
                    />
                  </div>
                  <div>
                    <Label>Difficoltà</Label>
                    <Select value={editing.difficulty ?? "Facile"} onValueChange={(v) => setEditing({ ...editing, difficulty: v as "Facile" | "Media" | "Esperta" })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Facile">Facile</SelectItem>
                        <SelectItem value="Media">Media</SelectItem>
                        <SelectItem value="Esperta">Esperta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tempo (min)</Label><Input type="number" value={editing.time_minutes ?? ""} onChange={(e) => setEditing({ ...editing, time_minutes: parseInt(e.target.value) || null })} /></div>
                  <div><Label>Porzioni</Label><Input type="number" value={editing.servings ?? ""} onChange={(e) => setEditing({ ...editing, servings: parseInt(e.target.value) || null })} /></div>
                </div>
                <ImageUploader
                  value={editing.cover_image ? [editing.cover_image] : []}
                  onChange={(v) => setEditing({ ...editing, cover_image: v[0] ?? null })}
                  bucket="recipe-images" label="Copertina"
                />
                <SortableImageGallery
                  value={editing.gallery}
                  onChange={(v) => setEditing({ ...editing, gallery: v })}
                  bucket="recipe-images" multiple label="Galleria (trascina per riordinare)"
                />
                <div><Label>Introduzione</Label><Textarea rows={3} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} /></div>
                <div>
                  <Label>Prodotti collegati</Label>
                  <p className="text-xs text-slate-500 mt-1">I prodotti collegati appariranno nella ricetta e nelle pagine dei singoli prodotti.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {editing.product_slugs.map((slug) => {
                      const p = products.find((x) => x.slug === slug);
                      return (
                        <span key={slug} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-brand-cream border border-brand-gold/30 text-xs">
                          {p?.title ?? slug}
                          <button type="button" onClick={() => setEditing({ ...editing, product_slugs: editing.product_slugs.filter((s) => s !== slug) })} className="text-slate-500 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <div className="mt-2">
                    <SpecCombobox
                      value=""
                      onChange={(v) => {
                        // v is product title; find slug
                        const p = products.find((x) => x.title === v);
                        if (p && !editing.product_slugs.includes(p.slug)) {
                          setEditing({ ...editing, product_slugs: [...editing.product_slugs, p.slug] });
                        }
                      }}
                      options={products.filter((p) => !editing.product_slugs.includes(p.slug)).map((p) => p.title)}
                      placeholder="+ aggiungi prodotto..."
                      emptyText="Nessun prodotto"
                    />
                  </div>
                </div>
                <div>
                  <Label>Ingredienti</Label>
                  <div className="space-y-2 mt-2">
                    {editing.ingredients.map((ing, i) => (
                      <div key={i} className="flex gap-2">
                        <Input className="w-28" placeholder="Qta" value={ing.qty} onChange={(e) => { const n = [...editing.ingredients]; n[i] = { ...n[i], qty: e.target.value }; setEditing({ ...editing, ingredients: n }); }} />
                        <Input placeholder="Ingrediente" value={ing.item} onChange={(e) => { const n = [...editing.ingredients]; n[i] = { ...n[i], item: e.target.value }; setEditing({ ...editing, ingredients: n }); }} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing({ ...editing, ingredients: editing.ingredients.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditing({ ...editing, ingredients: [...editing.ingredients, { qty: "", item: "" }] })}>+ aggiungi</Button>
                  </div>
                </div>
                <div>
                  <Label>Passaggi</Label>
                  <div className="space-y-2 mt-2">
                    {editing.steps.map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <span className="text-sm font-display text-brand-red w-6 pt-2">{i + 1}.</span>
                        <Textarea rows={2} value={s} onChange={(e) => { const n = [...editing.steps]; n[i] = e.target.value; setEditing({ ...editing, steps: n }); }} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing({ ...editing, steps: editing.steps.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditing({ ...editing, steps: [...editing.steps, ""] })}>+ aggiungi passo</Button>
                  </div>
                </div>
                <div><Label>Consigli dello chef</Label><Textarea rows={2} value={editing.chef_tips ?? ""} onChange={(e) => setEditing({ ...editing, chef_tips: e.target.value })} /></div>
                <div><Label>Abbinamenti</Label><Textarea rows={2} value={editing.pairings ?? ""} onChange={(e) => setEditing({ ...editing, pairings: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <label className="flex items-center justify-between p-3 border rounded-md"><span className="text-sm">In evidenza</span><Switch checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} /></label>
                  <label className="flex items-center justify-between p-3 border rounded-md"><span className="text-sm">Pubblicata</span><Switch checked={editing.status === "published"} onCheckedChange={(v) => setEditing({ ...editing, status: v ? "published" : "draft" })} /></label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSave} className="bg-brand-red hover:bg-brand-red-hover">Salva</Button>
                  <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AdminShell>
  );
}